import { forwardRef } from 'react';
import { Switch as RadixSwitch } from 'radix-ui';

export interface SwitchProps {
  /** Controlled on/off state. */
  checked: boolean;
  /** Fired with the next state; omit/disable to render a read-only toggle. */
  onCheckedChange?: (checked: boolean) => void;
  /** Accessible label (there is no visible <label> in the table rows). */
  label: string;
  disabled?: boolean;
  className?: string;
  /** Id(s) of the element(s) that describe the switch (hint text, inline error). */
  'aria-describedby'?: string;
}

/**
 * Accessible on/off toggle (Radix Switch + tokens, NFR-15). Used for the
 * per-member permission columns on the Team Access table (MYN-43); the
 * DAXE-seat column renders it `disabled` until the billing module lands.
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  {
    checked,
    onCheckedChange,
    label,
    disabled = false,
    className = '',
    'aria-describedby': describedBy,
  },
  ref,
) {
  return (
    <RadixSwitch.Root
      ref={ref}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={label}
      aria-describedby={describedBy}
      className={`relative inline-flex h-[18px] w-8 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dkgreen disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-dkgreen data-[state=unchecked]:bg-slate/30 ${className}`.trim()}
    >
      <RadixSwitch.Thumb className="block h-[14px] w-[14px] translate-x-0.5 rounded-full bg-white shadow transition-transform data-[state=checked]:translate-x-[16px]" />
    </RadixSwitch.Root>
  );
});
